'use client';

import { useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Download,
  Factory,
  FileText,
  Loader2,
  Network,
  PackageCheck,
  Plus,
  RotateCcw,
  Ship,
  Store,
  Trash2,
  TriangleAlert,
} from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Textarea } from '@kit/ui/textarea';

import { DateField } from '~/components/date-field';
import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';
import {
  type AddressInputsValue,
  EMPTY_ADDRESS_INPUTS,
  addressLines,
} from '~/lib/addresses/address-parts';
import { savePurchaseOrderDraft } from '~/lib/purchase-order-draft';

import { RelatedTools } from '../tools/_components/related-tools';
import { PurchaseOrderDocument } from './_components/purchase-order-document';
import { downloadPurchaseOrderPdf } from './_lib/generate-purchase-order-pdf';
import {
  CURRENCIES,
  INCOTERMS,
  type PurchaseOrder,
  type PurchaseOrderLineItem,
  type PurchaseOrderParty,
  calculatePurchaseOrderTotals,
  formatCurrency,
  getVisibleLineItems,
  parseAmount,
} from './_lib/purchase-order';

const baseUrl = appConfig.url;
const creatorUrl = `${baseUrl}/free-purchase-order-creator`;
const toolsUrl = `${baseUrl}/tools`;

/**
 * Slug-less catch-all: it resolves the visitor's workspace after signup and
 * forwards to `/home/[account]/orders/import-draft`.
 */
const IMPORT_DRAFT_PATH = '/dashboard/orders/import-draft';
const templatePath = '/tools/templates/purchase-order-template';

/** Days between the order date and the default ship-by date. */
const DEFAULT_LEAD_TIME_DAYS = 45;

/**
 * Well past any realistic PO, but bounded: every keystroke re-renders the
 * preview and re-runs the totals, and an unbounded list makes it trivial to
 * lock up the tab by holding down "Add line item".
 */
const MAX_LINE_ITEMS = 100;

/**
 * A party's printed address, one part per line, from the parts the creator
 * asks for. The preview and the PDF both read the composed string.
 */
function composePartyAddress(parts: AddressInputsValue) {
  return addressLines(parts).join('\n');
}

const BUYER_ADDRESS: AddressInputsValue = {
  address_line1: '1420 Harbor Boulevard',
  address_line2: 'Suite 300',
  address_line3: '',
  city: 'Long Beach',
  region: 'CA',
  postal_code: '90802',
  country: 'USA',
};

const SHIP_TO_ADDRESS: AddressInputsValue = {
  address_line1: '2200 Pier D Avenue',
  address_line2: 'Dock 14',
  address_line3: '',
  city: 'Long Beach',
  region: 'CA',
  postal_code: '90802',
  country: 'USA',
};

const SUPPLIER_ADDRESS: AddressInputsValue = {
  address_line1: 'No. 88 Jiangnan Road',
  address_line2: 'Yinzhou District',
  address_line3: '',
  city: 'Ningbo',
  region: 'Zhejiang',
  postal_code: '315100',
  country: 'China',
};

/**
 * Dates are left blank here and filled in on mount: computing "today" while
 * rendering would produce different markup on the server and the client.
 */
const DEFAULT_ORDER: PurchaseOrder = {
  buyer: {
    address: composePartyAddress(BUYER_ADDRESS),
    addressParts: BUYER_ADDRESS,
    contact: 'Alex Moreno, Procurement',
    email: 'po@northwindtrading.com',
    name: 'Northwind Trading Co.',
  },
  currency: 'USD',
  discount: '0',
  incoterm: 'FOB',
  lineItems: [
    {
      description: 'Stoneware mug, 350ml, matte white',
      hsCode: '691200',
      id: 'line-1',
      quantity: '2400',
      sku: 'MUG-350-WHT',
      unit: 'pcs',
      unitPrice: '2.15',
    },
    {
      description: 'Serving bowl, 18cm, sage glaze',
      hsCode: '691200',
      id: 'line-2',
      quantity: '1200',
      sku: 'BWL-18-SGE',
      unit: 'pcs',
      unitPrice: '3.40',
    },
    {
      description: 'Printed gift box, medium, 4-colour',
      hsCode: '481920',
      id: 'line-3',
      quantity: '3600',
      sku: 'GFT-BOX-M',
      unit: 'pcs',
      unitPrice: '0.42',
    },
  ],
  notes:
    'Mark every carton with the PO number and SKU on two adjacent sides. The ship-by date is the vessel cut-off, not the factory completion date. Any specification change requires written approval before production starts.',
  orderDate: '',
  paymentTerms: '30% deposit, 70% against copy B/L',
  poNumber: 'PO-1042',
  shipByDate: '',
  shipTo: {
    address: composePartyAddress(SHIP_TO_ADDRESS),
    addressParts: SHIP_TO_ADDRESS,
    contact: '',
    email: '',
    name: 'Northwind 3PL, Long Beach DC',
  },
  shippingCost: '0',
  supplier: {
    address: composePartyAddress(SUPPLIER_ADDRESS),
    addressParts: SUPPLIER_ADDRESS,
    contact: 'Lily Chen, Export Sales',
    email: 'lily@haoyuan-housewares.cn',
    name: 'Ningbo Haoyuan Housewares Co., Ltd',
  },
  taxPercent: '0',
};

const basicsFaqItems = [
  {
    value: 'free',
    question: 'Is this purchase order creator free?',
    answer:
      'Yes. You can build a purchase order and download it as a PDF with no signup, no watermark, and no usage limit. The document is generated in your browser, so nothing you type is sent to a server.',
  },
  {
    value: 'what-to-include',
    question: 'What should a purchase order include?',
    answer:
      'At minimum: a unique PO number, the order date, your company details, the supplier details, the delivery address, a line for each item with quantity and agreed unit price, the currency, the payment terms, and a required delivery or ship-by date. For imports, add the Incoterm and the HS code per line so freight quotes and customs paperwork can be prepared from the same document.',
  },
  {
    value: 'hs-codes',
    question: 'Why does the line item table have an HS code column?',
    answer:
      'The HS code determines the duty rate and customs classification for each item. Capturing it on the purchase order means your customs broker and freight forwarder can work from the PO directly instead of asking for the classification weeks later, when the goods are already moving.',
  },
  {
    value: 'incoterms',
    question: 'Which Incoterm should I put on a purchase order?',
    answer:
      'The Incoterm decides where the seller stops paying and you start. FOB and EXW are common for buyers arranging their own freight; DDP puts the entire delivered cost on the supplier. Whichever you choose, it needs to match the quote the price was based on. A price quoted EXW and a PO written DDP is a dispute waiting to happen.',
  },
  {
    value: 'po-numbering',
    question: 'How should I number purchase orders?',
    answer:
      'Use a short, sequential, never-reused format such as PO-1042. Some importers prefix by supplier or year. What matters is that the number is unique, appears on the carton markings and the invoice, and can be matched against the packing list when the shipment lands.',
  },
  {
    value: 'pdf-vs-excel',
    question: 'Should I send the PO as a PDF or an Excel file?',
    answer:
      'Send the PDF. It is the version of record and it cannot be quietly edited after you send it. Keep a spreadsheet for your own working copy if you want one, and the free Excel purchase order template covers that.',
  },
  {
    value: 'send-to-supplier',
    question: 'How do I get the purchase order to my supplier?',
    answer: `The PDF downloads straight to your device, so you attach it to an email the way you would any other document. That costs nothing and needs no account. If you would rather not track it by hand afterwards, you can save the same purchase order into ${appConfig.name}, where it stays alongside the documents, freight, and delivery for that order instead of scattered across an inbox, a folder, and a spreadsheet.`,
  },
  {
    value: 'legally-binding',
    question: 'Is a purchase order legally binding?',
    answer:
      'A purchase order generally becomes binding once the supplier accepts it, which is why the acceptance and the terms on the document matter. This tool produces a standard commercial PO, not legal advice, so have your own terms and conditions reviewed if you are placing significant orders.',
  },
] as const;

const sourcingFaqItems = [
  {
    value: 'alibaba',
    question: 'Do I need a purchase order if I am buying on Alibaba?',
    answer:
      'Alibaba orders usually run on a proforma invoice the supplier writes, and Trade Assurance records the agreement on the platform. Sending your own purchase order first is still worth the ten minutes: it puts your specification, quantity, price, and ship-by date in your words before the supplier puts theirs in writing. When the proforma invoice comes back, compare it against your PO line by line. Every difference is something to settle before you pay a deposit.',
  },
  {
    value: 'po-vs-proforma',
    question:
      'What is the difference between a purchase order and a proforma invoice?',
    answer:
      'You issue the purchase order; the supplier issues the proforma invoice. The PO is your offer, stating what you want, at what price, by when. The proforma invoice is the supplier confirming it, usually with their bank details attached for the deposit. Both documents should carry the same PO number, and any figure that differs between them needs resolving before money moves.',
  },
  {
    value: 'chinese-supplier',
    question: 'How do I write a purchase order for a Chinese supplier?',
    answer:
      'Write it in English, state the currency explicitly (usually USD), and name the Incoterm. FOB with the port named is the most common arrangement for a first order. Give a ship-by date rather than a lead time in days, put an HS code on every line, and describe material, size, colour, and packaging in the description instead of writing "as per sample". Say how cartons must be marked, because that is what your freight forwarder and your warehouse will look for when the goods arrive.',
  },
  {
    value: 'includes-shipping',
    question: 'Does a purchase order include shipping?',
    answer:
      'It depends on the Incoterm. On EXW or FOB the unit price excludes international freight, so you leave the shipping field at zero and pay your forwarder separately. On CIF, DAP, or DDP the supplier arranges some or all of the transport, so either the unit price already covers it or the supplier quotes freight separately, and that figure goes in the shipping and freight field. Either way the PO total is not your landed cost: duty, customs clearance, and delivery to the warehouse still sit on top of it.',
  },
  {
    value: 'deposit',
    question: 'What payment terms should a first order use?',
    answer:
      'The common structure for imports is a deposit against the purchase order and the balance before the goods ship or against a copy bill of lading, and 30% and 70% is the usual split. Paying the full amount up front removes your only real leverage if quality is wrong, and most established suppliers will not expect it. Whatever you agree goes in the payment terms field so the PO and the proforma invoice say the same thing.',
  },
  {
    value: 'amazon-fba',
    question: 'Do I need a purchase order for Amazon FBA inventory?',
    answer:
      'Amazon does not ask for one. FBA needs a shipping plan, not your PO. The purchase order is for your supplier and for your own records. For FBA specifically, put the prep requirements on the PO itself: FNSKU labels, polybags with the suffocation warning, carton weight and dimension limits, and whether the supplier ships to your prep centre or straight to a fulfilment centre. Agreeing prep after production has already run is one of the more expensive ways to learn this.',
  },
  {
    value: 'amazon-vendor',
    question: 'Is this the same as an Amazon Vendor Central purchase order?',
    answer:
      'No. A Vendor Central PO is one Amazon issues to you when you sell to Amazon wholesale. This tool creates purchase orders you issue to your own suppliers, which is what a private label, FBA, or ecommerce seller raises when buying stock.',
  },
  {
    value: 'shopify',
    question: 'Do I need purchase orders for a Shopify store?',
    answer:
      'If you hold your own stock, yes. The PO is what fixes the price and the delivery date before you pay, and it is what you reconcile the supplier invoice and the delivered quantity against. If you are dropshipping and never handle the inventory, you generally will not raise a PO for individual customer orders, but you still want one the moment you place a bulk buy or commission a custom production run.',
  },
  {
    value: 'moq',
    question: 'Should the MOQ go on the purchase order?',
    answer:
      'The quantity you are actually ordering goes on the PO, not the supplier minimum. If you negotiated a price that depends on hitting a volume, note it in the terms. A unit price quoted at 5,000 pieces does not survive an order for 1,200, and it is better to have that written down than discovered on the proforma invoice.',
  },
] as const;

const allFaqItems = [...basicsFaqItems, ...sourcingFaqItems];

const beginnerScenarios = [
  {
    icon: Factory,
    title: 'Buying from a Chinese supplier or Alibaba',
    points: [
      'Name the Incoterm and port, as in FOB Ningbo, not just "FOB".',
      'Give a ship-by date, not a lead time in days.',
      'Describe the product properly instead of "as per sample".',
      'Send your PO before you accept their proforma invoice.',
    ],
  },
  {
    icon: PackageCheck,
    title: 'Ordering stock for Amazon FBA',
    points: [
      'Put FNSKU labelling and polybag requirements in the notes.',
      'State carton weight and dimension limits up front.',
      'Say where it ships, to a prep centre or a fulfilment centre.',
      'Keep the PO number on the cartons so receiving reconciles.',
    ],
  },
  {
    icon: Store,
    title: 'Stocking a Shopify or DTC store',
    points: [
      'Raise a PO for every bulk buy, even from a small supplier.',
      'Match the supplier invoice to the PO before you pay it.',
      'Track the ship-by date against your launch or restock date.',
      'Keep unit prices on the PO, because they are your real COGS input.',
    ],
  },
] as const;

function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function withDefaultDates(order: PurchaseOrder): PurchaseOrder {
  const today = new Date();
  const shipBy = new Date(today);

  shipBy.setDate(shipBy.getDate() + DEFAULT_LEAD_TIME_DAYS);

  return {
    ...order,
    orderDate: toIsoDate(today),
    shipByDate: toIsoDate(shipBy),
  };
}

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Free Purchase Order Creator',
    alternateName: 'Purchase Order Generator',
    description:
      'Free online purchase order creator. Fill in supplier, ship-to, line items, HS codes, Incoterms, tax, and freight, then download the purchase order as a PDF.',
    url: creatorUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    creator: { '@type': 'Organization', name: appConfig.name, url: baseUrl },
    featureList: [
      'Create a purchase order online',
      'Download a purchase order as PDF',
      'Add unlimited line items with SKU, HS code, quantity, and unit price',
      'Set Incoterms, payment terms, and ship-by dates',
      'Apply discount, tax, and freight to the order total',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Free Tools', item: toolsUrl },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Purchase Order Creator',
        item: creatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allFaqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

function Field({
  currency,
  id,
  label,
  max,
  maxLength,
  onChange,
  placeholder,
  prefix,
  suffix,
  type = 'text',
  value,
}: {
  /** Set on a `date` field, where it decides day-first versus month-first. */
  currency?: string | null;
  id: string;
  label: string;
  max?: string;
  maxLength?: number;
  onChange: (value: string) => void;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  type?: string;
  value: string;
}) {
  /*
   * A PO built here is printed and emailed to a factory. `<input type="date">`
   * writes the date in the *browser's* locale, so a UK buyer filling this in
   * produced a document their supplier read month-first, and neither side had
   * any way to tell from the page. The order's own currency settles it.
   */
  if (type === 'date') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} className="text-foreground text-xs font-bold">
          {label}
        </Label>
        <DateField
          id={id}
          currency={currency}
          max={max}
          value={value}
          onChange={onChange}
          className="[&_input]:border-border [&_input]:bg-muted [&_input]:h-11 [&_input]:rounded-xl [&_input]:shadow-none"
        />
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-foreground text-xs font-bold">
        {label}
      </Label>
      <div className="relative">
        {prefix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm"
          >
            {prefix}
          </span>
        ) : null}
        <Input
          id={id}
          type={type}
          inputMode={type === 'number' ? 'decimal' : undefined}
          min={type === 'number' ? '0' : undefined}
          max={max}
          maxLength={maxLength}
          step={type === 'number' ? '0.01' : undefined}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`border-border bg-muted h-11 rounded-xl shadow-none ${
            prefix ? 'pl-7' : ''
          } ${suffix ? 'pr-8' : ''}`}
        />
        {suffix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm"
          >
            {suffix}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function TextAreaField({
  id,
  label,
  maxLength,
  onChange,
  placeholder,
  rows = 3,
  value,
}: {
  id: string;
  label: string;
  maxLength?: number;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  value: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-foreground text-xs font-bold">
        {label}
      </Label>
      <Textarea
        id={id}
        maxLength={maxLength}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="border-border bg-muted rounded-xl shadow-none"
      />
    </div>
  );
}

function PartyFields({
  idPrefix,
  onChange,
  party,
  showContact = true,
}: {
  idPrefix: string;
  onChange: (patch: Partial<PurchaseOrderParty>) => void;
  party: PurchaseOrderParty;
  showContact?: boolean;
}) {
  /*
   * A party that arrived with only a printed address — none do today, but the
   * type allows it — opens with that address in line 1 rather than losing it.
   */
  const parts = party.addressParts ?? {
    ...EMPTY_ADDRESS_INPUTS,
    address_line1: party.address,
  };

  const updateAddress = (patch: Partial<AddressInputsValue>) => {
    const next = { ...parts, ...patch };

    onChange({ addressParts: next, address: composePartyAddress(next) });
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field
          id={`${idPrefix}-name`}
          label="Company name"
          value={party.name}
          onChange={(value) => onChange({ name: value })}
        />
      </div>
      {/*
        The same seven fields every address form in the app asks for, so a
        purchase order carries the city and the postcode on their own rather
        than wherever they happened to fall in one box. Each keystroke
        recomposes the printed address the preview and the PDF read.
      */}
      <div className="sm:col-span-2">
        <Field
          id={`${idPrefix}-address-line1`}
          label="Address 1"
          placeholder="Street address"
          value={parts.address_line1}
          onChange={(value) => updateAddress({ address_line1: value })}
        />
      </div>
      <div className="sm:col-span-2">
        <Field
          id={`${idPrefix}-address-line2`}
          label="Address 2"
          placeholder="Building, floor, unit (optional)"
          value={parts.address_line2}
          onChange={(value) => updateAddress({ address_line2: value })}
        />
      </div>
      <div className="sm:col-span-2">
        <Field
          id={`${idPrefix}-address-line3`}
          label="Address 3"
          placeholder="District, industrial park (optional)"
          value={parts.address_line3}
          onChange={(value) => updateAddress({ address_line3: value })}
        />
      </div>
      <Field
        id={`${idPrefix}-city`}
        label="City"
        value={parts.city}
        onChange={(value) => updateAddress({ city: value })}
      />
      <Field
        id={`${idPrefix}-region`}
        label="Region / State"
        value={parts.region}
        onChange={(value) => updateAddress({ region: value })}
      />
      <Field
        id={`${idPrefix}-postal-code`}
        label="Zip / Postal Code"
        value={parts.postal_code}
        onChange={(value) => updateAddress({ postal_code: value })}
      />
      <Field
        id={`${idPrefix}-country`}
        label="Country"
        value={parts.country}
        onChange={(value) => updateAddress({ country: value })}
      />
      {showContact ? (
        <>
          <Field
            id={`${idPrefix}-contact`}
            label="Contact name"
            value={party.contact}
            onChange={(value) => onChange({ contact: value })}
          />
          <Field
            id={`${idPrefix}-email`}
            label="Email"
            type="email"
            value={party.email}
            onChange={(value) => onChange({ email: value })}
          />
        </>
      ) : null}
    </div>
  );
}

function FaqAccordion({
  defaultValue,
  items,
}: {
  defaultValue?: string;
  items: ReadonlyArray<{ answer: string; question: string; value: string }>;
}) {
  return (
    <div className="mt-6">
      <Accordion type="single" collapsible defaultValue={defaultValue}>
        {items.map((item) => (
          <AccordionItem
            key={item.value}
            value={item.value}
            className="border-border bg-card mb-4 overflow-hidden rounded-2xl border px-6"
          >
            <AccordionTrigger className="py-6 text-left text-base font-bold hover:no-underline">
              {item.question}
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
              {item.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function Section({
  children,
  description,
  title,
}: React.PropsWithChildren<{ description?: string; title: string }>) {
  return (
    <section className="border-border bg-card rounded-2xl border p-5 sm:p-6">
      <h3 className="text-base font-extrabold">{title}</h3>
      {description ? (
        <p className="text-muted-foreground mt-1 text-xs leading-5">
          {description}
        </p>
      ) : null}
      <div className="mt-5">{children}</div>
    </section>
  );
}

export default function PurchaseOrderCreatorPage() {
  const [order, setOrder] = useState<PurchaseOrder>(DEFAULT_ORDER);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const nextLineId = useRef(order.lineItems.length + 1);
  const router = useRouter();

  const startFreePath = pathsConfig.auth.startFree;
  const totals = calculatePurchaseOrderTotals(order);
  const currency = order.currency || 'USD';

  useEffect(() => {
    setOrder((current) => withDefaultDates(current));
  }, []);

  function updateOrder(patch: Partial<PurchaseOrder>) {
    setOrder((current) => ({ ...current, ...patch }));
  }

  function updateParty(
    key: 'buyer' | 'shipTo' | 'supplier',
    patch: Partial<PurchaseOrderParty>,
  ) {
    setOrder((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  }

  function updateLineItem(
    id: string,
    patch: Partial<Omit<PurchaseOrderLineItem, 'id'>>,
  ) {
    setOrder((current) => ({
      ...current,
      lineItems: current.lineItems.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    }));
  }

  function addLineItem() {
    if (order.lineItems.length >= MAX_LINE_ITEMS) {
      return;
    }

    const id = `line-${nextLineId.current}`;

    nextLineId.current += 1;

    setOrder((current) => ({
      ...current,
      lineItems: [
        ...current.lineItems,
        {
          description: '',
          hsCode: '',
          id,
          quantity: '',
          sku: '',
          unit: 'pcs',
          unitPrice: '',
        },
      ],
    }));
  }

  function removeLineItem(id: string) {
    setOrder((current) => ({
      ...current,
      lineItems:
        current.lineItems.length > 1
          ? current.lineItems.filter((item) => item.id !== id)
          : current.lineItems,
    }));
  }

  function resetExample() {
    nextLineId.current = DEFAULT_ORDER.lineItems.length + 1;
    setDownloadError(null);
    setOrder(withDefaultDates(DEFAULT_ORDER));
  }

  async function handleDownload() {
    setIsGenerating(true);
    setDownloadError(null);

    try {
      await downloadPurchaseOrderPdf(order, appConfig.name);
    } catch (error) {
      console.error('Purchase order PDF generation failed', error);

      setDownloadError(
        'The PDF could not be generated in this browser. Try again, or download the Excel template instead.',
      );
    } finally {
      setIsGenerating(false);
    }
  }

  /**
   * Parks the PO in localStorage and sends the visitor to signup. `/dashboard`
   * is the slug-less catch-all that resolves their workspace afterwards, so
   * this one URL works whether or not they already have an account.
   */
  function handleSaveToAccount() {
    setDownloadError(null);

    const visible = getVisibleLineItems(order);

    if (visible.length === 0) {
      setDownloadError(
        'Add at least one line item before saving this purchase order to an account.',
      );

      return;
    }

    const saved = savePurchaseOrderDraft({
      currency,
      incoterm: order.incoterm.trim(),
      lines: visible.map(({ index, item }) => ({
        description: item.description.trim(),
        hsCode: item.hsCode.trim(),
        lineTotal: totals.lineTotals[index] ?? 0,
        quantity: parseAmount(item.quantity),
        sku: item.sku.trim(),
        unit: item.unit.trim(),
        unitPrice: parseAmount(item.unitPrice),
      })),
      notes: order.notes.trim(),
      orderDate: order.orderDate,
      paymentTerms: order.paymentTerms.trim(),
      poNumber: order.poNumber.trim(),
      shipByDate: order.shipByDate,
      supplierEmail: order.supplier.email.trim(),
      supplierName: order.supplier.name.trim(),
      total: totals.total,
    });

    if (!saved) {
      setDownloadError(
        'This purchase order could not be saved for transfer, because your browser is blocking local storage. Download the PDF instead.',
      );

      return;
    }

    router.push(
      `${pathsConfig.auth.startFree}?next=${encodeURIComponent(IMPORT_DRAFT_PATH)}`,
    );
  }

  return (
    <>
      <StructuredData />

      {/* Sections alternate across the theme's surface ramp — background,
          muted, and one sage band — so the page reads as distinct chapters
          rather than one continuous sheet. Every token has a dark-mode pair. */}
      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.13*100%),transparent),_transparent_44%)]" />

          <div className="relative mx-auto max-w-7xl px-4 pt-16 pb-10 sm:px-6 lg:px-8 lg:pt-20">
            <div className="max-w-3xl">
              <div className="bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                <ClipboardList className="h-4 w-4" />
                Free purchase order creator
              </div>

              <h1 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Create a <span className="text-primary">Purchase Order</span>{' '}
                and download the PDF
              </h1>
              <p className="text-muted-foreground mt-6 max-w-2xl text-lg leading-8">
                Fill in your supplier, delivery address, and line items, and the
                document updates as you type. Download a clean, professional
                purchase order PDF you can send to a supplier straight away. No
                signup, no watermark. When you are tired of filling it in,
                SupplyAutomate drafts the next one from your last order and
                waits for you to approve it.
              </p>

              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                {[
                  'Free to use, no signup required',
                  'HS codes and Incoterms built in',
                  'Save it to a workspace when you want to track it',
                ].map((item) => (
                  <span
                    key={item}
                    className="text-muted-foreground inline-flex items-center gap-2 text-sm font-medium"
                  >
                    <CheckCircle2 className="text-primary h-4 w-4" />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* The working surface sits one step darker than the page so the white
            form and document cards read as raised. */}
        <section className="border-border bg-muted border-b px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
              <form
                onSubmit={(event) => event.preventDefault()}
                className="space-y-5"
              >
                <Section
                  title="Order details"
                  description="The PO number is what everything downstream gets matched against, from invoices to packing lists to carton markings."
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      id="poNumber"
                      label="PO number"
                      value={order.poNumber}
                      onChange={(value) => updateOrder({ poNumber: value })}
                    />
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="currency"
                        className="text-foreground text-xs font-bold"
                      >
                        Currency
                      </Label>
                      <Select
                        value={order.currency}
                        onValueChange={(value) =>
                          updateOrder({ currency: value })
                        }
                      >
                        <SelectTrigger
                          id="currency"
                          className="border-border bg-muted !h-11 rounded-xl shadow-none"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Field
                      id="orderDate"
                      label="Order date"
                      type="date"
                      currency={order.currency}
                      value={order.orderDate}
                      onChange={(value) => updateOrder({ orderDate: value })}
                    />
                    <Field
                      id="shipByDate"
                      label="Ship by"
                      type="date"
                      currency={order.currency}
                      value={order.shipByDate}
                      onChange={(value) => updateOrder({ shipByDate: value })}
                    />
                  </div>
                </Section>

                <Section
                  title="Your company"
                  description="This appears at the top of the purchase order as the buying party."
                >
                  <PartyFields
                    idPrefix="buyer"
                    party={order.buyer}
                    onChange={(patch) => updateParty('buyer', patch)}
                  />
                </Section>

                <Section title="Supplier">
                  <PartyFields
                    idPrefix="supplier"
                    party={order.supplier}
                    onChange={(patch) => updateParty('supplier', patch)}
                  />
                </Section>

                <Section
                  title="Ship to"
                  description="Where the goods are actually delivered, often a 3PL or warehouse rather than your office."
                >
                  <PartyFields
                    idPrefix="shipTo"
                    party={order.shipTo}
                    showContact={false}
                    onChange={(patch) => updateParty('shipTo', patch)}
                  />
                </Section>

                <Section
                  title="Line items"
                  description="One line per SKU. Line totals calculate as you type."
                >
                  <div className="space-y-4">
                    {order.lineItems.map((item, index) => (
                      <div
                        key={item.id}
                        className="border-border bg-muted rounded-xl border p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-muted-foreground text-xs font-bold tracking-[0.14em] uppercase">
                            Line {index + 1}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={order.lineItems.length === 1}
                            onClick={() => removeLineItem(item.id)}
                            className="text-muted-foreground hover:text-destructive h-8 px-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">
                              Remove line {index + 1}
                            </span>
                          </Button>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-6">
                          <div className="sm:col-span-2">
                            <Field
                              id={`${item.id}-sku`}
                              label="SKU"
                              value={item.sku}
                              onChange={(value) =>
                                updateLineItem(item.id, { sku: value })
                              }
                            />
                          </div>
                          <div className="sm:col-span-4">
                            <Field
                              id={`${item.id}-description`}
                              label="Description"
                              value={item.description}
                              onChange={(value) =>
                                updateLineItem(item.id, { description: value })
                              }
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <Field
                              id={`${item.id}-hsCode`}
                              label="HS code"
                              value={item.hsCode}
                              onChange={(value) =>
                                updateLineItem(item.id, { hsCode: value })
                              }
                            />
                          </div>
                          <div className="sm:col-span-1">
                            <Field
                              id={`${item.id}-quantity`}
                              label="Qty"
                              type="number"
                              value={item.quantity}
                              onChange={(value) =>
                                updateLineItem(item.id, { quantity: value })
                              }
                            />
                          </div>
                          <div className="sm:col-span-1">
                            <Field
                              id={`${item.id}-unit`}
                              label="Unit"
                              value={item.unit}
                              onChange={(value) =>
                                updateLineItem(item.id, { unit: value })
                              }
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <Field
                              id={`${item.id}-unitPrice`}
                              label="Unit price"
                              type="number"
                              value={item.unitPrice}
                              onChange={(value) =>
                                updateLineItem(item.id, { unitPrice: value })
                              }
                            />
                          </div>
                        </div>

                        <div className="text-muted-foreground mt-3 flex justify-end text-xs">
                          Line total:{' '}
                          <span className="text-foreground ml-1.5 font-bold tabular-nums">
                            {formatCurrency(
                              totals.lineTotals[index] ?? 0,
                              currency,
                            )}
                          </span>
                        </div>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      disabled={order.lineItems.length >= MAX_LINE_ITEMS}
                      onClick={addLineItem}
                      className="w-full rounded-xl"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add line item
                    </Button>
                  </div>
                </Section>

                <Section
                  title="Terms, tax and freight"
                  description="The Incoterm has to match the quote your unit price came from."
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="incoterm"
                        className="text-foreground text-xs font-bold"
                      >
                        Incoterm
                      </Label>
                      <Select
                        value={order.incoterm}
                        onValueChange={(value) =>
                          updateOrder({ incoterm: value })
                        }
                      >
                        <SelectTrigger
                          id="incoterm"
                          className="border-border bg-muted !h-11 rounded-xl shadow-none"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INCOTERMS.map((code) => (
                            <SelectItem key={code} value={code}>
                              {code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Field
                      id="paymentTerms"
                      label="Payment terms"
                      value={order.paymentTerms}
                      onChange={(value) => updateOrder({ paymentTerms: value })}
                    />
                    <Field
                      id="discount"
                      label="Discount"
                      type="number"
                      value={order.discount}
                      onChange={(value) => updateOrder({ discount: value })}
                    />
                    <Field
                      id="taxPercent"
                      label="Tax"
                      type="number"
                      max="100"
                      suffix="%"
                      value={order.taxPercent}
                      onChange={(value) => updateOrder({ taxPercent: value })}
                    />
                    <div className="sm:col-span-2">
                      <Field
                        id="shippingCost"
                        label="Shipping / freight"
                        type="number"
                        value={order.shippingCost}
                        onChange={(value) =>
                          updateOrder({ shippingCost: value })
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <TextAreaField
                        id="notes"
                        label="Notes and terms"
                        maxLength={2000}
                        rows={4}
                        value={order.notes}
                        onChange={(value) => updateOrder({ notes: value })}
                      />
                    </div>
                  </div>
                </Section>
              </form>

              {/* The sticky column scrolls internally once its content is
                  taller than the viewport. Without this the card below the
                  preview sits permanently past the fold on shorter screens —
                  sticky content moves with the page, so no amount of page
                  scrolling can bring it back into view. */}
              <div className="lg:sticky lg:top-8 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-extrabold">Live preview</h2>
                    <p className="text-muted-foreground text-xs">
                      This is exactly what the PDF will look like.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetExample}
                      className="rounded-xl"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                    <Button
                      type="button"
                      onClick={handleDownload}
                      disabled={isGenerating}
                      className="rounded-xl"
                    >
                      {isGenerating ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      {isGenerating ? 'Generating…' : 'Download PDF'}
                    </Button>
                  </div>
                </div>

                {downloadError ? (
                  <p
                    role="alert"
                    className="border-destructive/30 bg-destructive/10 text-destructive mb-4 flex items-start gap-2 rounded-xl border p-3 text-xs leading-5"
                  >
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    {downloadError}
                  </p>
                ) : null}

                <div
                  role="region"
                  aria-label="Purchase order preview"
                  aria-live="polite"
                  className="max-h-[70vh] overflow-y-auto lg:max-h-none lg:overflow-visible"
                >
                  <PurchaseOrderDocument order={order} totals={totals} />
                </div>

                <div className="border-border bg-card mt-5 rounded-2xl border p-5">
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-bold">
                        Keep this PO instead of re-typing it
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs leading-5">
                        Downloading the PDF here is free and always will be.
                        Saving it to {appConfig.name} carries this exact order,
                        with supplier, line items, prices, and terms, into your
                        workspace, where it stays alongside your documents,
                        freight, and delivery instead of in a folder somewhere.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSaveToAccount}
                        className="mt-3 rounded-lg"
                      >
                        Save this PO to my account
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight">
              What a purchase order has to get right
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-3">
              {[
                {
                  icon: ClipboardList,
                  title: 'A number everything matches against',
                  description:
                    'The PO number should appear on the supplier invoice, the packing list, and the carton markings. Without it, reconciling a short shipment is guesswork.',
                },
                {
                  icon: Ship,
                  title: 'The Incoterm and the ship-by date',
                  description:
                    'These two decide who pays for what and when the goods must leave. A price agreed FOB and a PO written DDP is a dispute you will have later.',
                },
                {
                  icon: FileText,
                  title: 'HS codes on every line',
                  description:
                    'Customs classification drives your duty rate. Capturing it on the PO means your broker is not asking for it once the container is already at sea.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="border-border bg-card rounded-2xl border p-5"
                >
                  <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-xl">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-sm font-bold">{item.title}</h3>
                  <p className="text-muted-foreground mt-2 text-xs leading-6">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground mt-8 text-sm leading-7">
              Prefer to work in a spreadsheet? The same document is available as
              a{' '}
              <Link
                href={templatePath}
                className="text-primary font-semibold hover:underline"
              >
                free Excel purchase order template
              </Link>
              , with the line totals already wired up.
            </p>
          </div>
        </section>

        {/* The beginner block gets the one tinted band on the page: it is
            editorial rather than product copy, and the change of hue is what
            stops it reading as more of the section above. Coral tint rather
            than a cool colour, so it stays inside the brand ramp. */}
        <section className="border-border bg-accent-tint border-y px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight">
              Sending your first purchase order
            </h2>
            <p className="text-muted-foreground mt-4 max-w-3xl text-sm leading-7">
              If this is the first PO you have ever raised, the document is not
              the hard part. Knowing what belongs on it is. A supplier will
              quote against whatever you leave vague, and the things people
              forget are almost always the same three: what the price actually
              covers, when the goods have to leave, and how the cartons are
              labelled.
            </p>

            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {beginnerScenarios.map((scenario) => (
                <div
                  key={scenario.title}
                  className="border-border bg-card rounded-2xl border p-6"
                >
                  <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-xl">
                    <scenario.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-sm font-bold">{scenario.title}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {scenario.points.map((point) => (
                      <li
                        key={point}
                        className="text-muted-foreground flex gap-2.5 text-xs leading-6"
                      >
                        <CheckCircle2 className="text-primary mt-1 h-3.5 w-3.5 shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground mt-8 text-sm leading-7">
              One thing worth being clear on before you send it: the purchase
              order total is not what the stock ends up costing you. Unless you
              are buying DDP, freight, duty, customs clearance, and delivery to
              the warehouse all land afterwards, often weeks later, on separate
              invoices. Work out that number with the{' '}
              <Link
                href="/tools/landed-cost-calculator"
                className="text-primary font-semibold hover:underline"
              >
                landed cost calculator
              </Link>{' '}
              before you price the product.
            </p>
          </div>
        </section>

        {/* Muted, not background: the accordion rows are white cards and need a
            surface to sit on. */}
        <section id="faq" className="bg-muted px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-heading text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Purchase Order Creator FAQ
            </h2>

            <h3 className="mt-12 text-lg font-extrabold">
              Purchase order basics
            </h3>
            <FaqAccordion defaultValue="free" items={basicsFaqItems} />

            <h3 className="mt-12 text-lg font-extrabold">
              Suppliers, Alibaba, Amazon FBA and Shopify
            </h3>
            <FaqAccordion items={sourcingFaqItems} />
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-28">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />

          <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
            <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
              The PO is the easy part
            </p>
            <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl">
              Then it has to actually arrive.
            </h2>
            <p className="text-background/70 mx-auto mt-6 max-w-2xl text-lg leading-8">
              This creator is free to use, so build a PO and download it as
              often as you like. When you want the rest of it handled, save it
              into {appConfig.name}, which keeps purchase orders, suppliers,
              documents, freight, duty, and prep in one workspace, so a PO you
              send today is still telling you the true landed cost per unit when
              the container lands.
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                asChild
                className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
              >
                <Link href={startFreePath}>Start Your 14-Day Trial</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
              >
                <Link href="/#features">See how it works</Link>
              </Button>
            </div>

            <div className="text-background/60 mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                (item) => (
                  <span key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="text-primary h-4 w-4" />
                    {item}
                  </span>
                ),
              )}
            </div>
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <RelatedTools currentPath="/free-purchase-order-creator" />
        </section>
      </div>
    </>
  );
}
